import React, { createContext, useContext, useEffect, useState } from 'react';

import { handleCookie } from '@utils';

import { cookieOptions } from './helpers';

type ContextType = {
  accept: string[];
  selectedCookies: string[];
  setSelectedCookies: React.Dispatch<React.SetStateAction<string[]>>;
  acceptAllCookies(): void;
  acceptSelectedCookies(): void;
  acceptRequiredCookies(): void;
};
type CookieContextType = () => ContextType;

const Context = createContext<ContextType>({} as ContextType);

const CookieContext: CustomFC = ({ children }) => {
  const { getCookie, setCookie } = handleCookie;
  const [accept, setAccept] = useState(() => {
    const cookieValue: string = getCookie('mercuryoCookiesAccepted');
    return cookieValue ? cookieValue.split('|').filter(Boolean) : [];
  });
  const [selectedCookies, setSelectedCookies] = useState<string[]>([]);

  const setCookiesAccepted = (value: string[]) => {
    setCookie('mercuryoCookiesAccepted', value.join('|'), { expires: 3650 });
    setAccept(value);
  };

  useEffect(() => {
    setCookiesAccepted(accept);
  }, []);

  const acceptAllCookies = () => {
    const all = cookieOptions.map(cookie => cookie.id);
    setCookiesAccepted(all);
  };

  const acceptSelectedCookies = () => {
    const requiredCookies = cookieOptions
      .filter(cookie => !!cookie.isAllow)
      .map(cookie => cookie.id);
    setCookiesAccepted([...requiredCookies, ...selectedCookies]);
  };

  const acceptRequiredCookies = () => {
    const required = cookieOptions.filter(cookie => !!cookie.isAllow).map(cookie => cookie.id);
    setCookiesAccepted(required);
  };

  return (
    <Context.Provider
      value={{
        accept,
        selectedCookies,
        setSelectedCookies,
        acceptAllCookies,
        acceptSelectedCookies,
        acceptRequiredCookies,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useCookieContext: CookieContextType = () => useContext(Context);
export default CookieContext;
